import PropTypes from 'prop-types';
import React from 'react';
import { describePeriod } from '../../logic/statisticDefinition';
import {
  SUPPRESSED_VALUE,
  buildStatisticTable,
} from '../../logic/statisticTable';

const CAPTION_ID = 'statistic-result-caption';

function HeaderCells({ headers, rowSpan }) {
  return headers.map((header, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <th key={`row-header-${index}`} scope="col" rowSpan={rowSpan}>
      {header}
    </th>
  ));
}

function BodyRows({ rows }) {
  return rows.map((row) => (
    <tr key={row.key}>
      {row.headers.map((header, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <th key={`h-${index}`} scope="row">
          {header}
        </th>
      ))}
      {row.cells.map((cell, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <td key={`c-${index}`}>{cell}</td>
      ))}
    </tr>
  ));
}

function FlatTable({ table, caption }) {
  return (
    <table className="Statistics-Table">
      <caption id={CAPTION_ID}>{caption}</caption>
      <thead>
        <tr>
          <HeaderCells headers={table.rowHeaders} />
          {table.measureHeaders.map((header) => (
            <th key={header} scope="col">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <BodyRows rows={table.rows} />
      </tbody>
      <tfoot>
        <tr>
          <th scope="row" colSpan={table.rowHeaders.length}>
            Gesamt
          </th>
          {table.total.cells.map((cell, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <td key={`t-${index}`}>{cell}</td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
}

function CrossTable({ table, caption }) {
  const measureCount = table.measureHeaders.length;

  return (
    <table className="Statistics-Table">
      <caption id={CAPTION_ID}>{caption}</caption>
      <colgroup span={table.rowHeaders.length} />
      {table.columnValues.map((value) => (
        <colgroup key={value} span={measureCount} />
      ))}
      <thead>
        <tr>
          <HeaderCells headers={table.rowHeaders} rowSpan={2} />
          {table.columnValues.map((value) => (
            <th key={value} scope="colgroup" colSpan={measureCount}>
              {value}
            </th>
          ))}
        </tr>
        <tr>
          {table.columnValues.map((value) =>
            table.measureHeaders.map((header) => (
              <th key={`${value}-${header}`} scope="col">
                {header}
              </th>
            )),
          )}
        </tr>
      </thead>
      <tbody>
        <BodyRows rows={table.rows} />
      </tbody>
      <tfoot>
        <tr>
          {/* Der Server liefert Gesamtwerte nur ueber alle Faelle, nicht je
              Spalte: Bei Mehrfachzuordnung waeren Spaltensummen falsch. */}
          <th scope="row" colSpan={table.rowHeaders.length}>
            Gesamt
          </th>
          <td colSpan={Math.max(table.columnValues.length * measureCount, 1)}>
            {table.measureHeaders.map((header, index) => (
              <span key={header} className="Statistics-TotalItem">
                {header}: {table.total.cells[index]}
              </span>
            ))}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function captionFor(table) {
  if (table.isCrossTable) {
    return `Kreuztabelle – Spalten: ${table.columnHeader}`;
  }
  return 'Ergebnistabelle';
}

/**
 * Zeigt ein Auswertungsergebnis als Tabelle oder Kreuztabelle (CARE-814).
 * Breite Kreuztabellen scrollen in ihrem eigenen Bereich, nicht die Seite.
 */
function ResultTable({ result, catalog, definition, columnDimensionKey }) {
  const table = buildStatisticTable(result, catalog, columnDimensionKey);
  const caption = captionFor(table);
  const noCases = result && result.CaseCount === 0;

  return (
    <section className="Statistics-Result" aria-labelledby="statistic-result">
      <h2 id="statistic-result" className="Statistics-Heading">
        Ergebnis
      </h2>
      <p className="Statistics-Note">
        Zeitraum (Erstkontakt): <b>{describePeriod(definition)}</b>
      </p>
      <p className="Statistics-Note">
        Ausgewertete Fälle: <b>{table.caseCount}</b>
      </p>
      {noCases && (
        <p className="Statistics-Note">
          Im gewählten Zeitraum gibt es keine Fälle.
        </p>
      )}
      <div
        className="Statistics-TableScroll"
        role="region"
        aria-labelledby={CAPTION_ID}
        // Der Bereich muss per Tastatur erreichbar sein, sonst laesst sich eine
        // breite Kreuztabelle ohne Maus nicht seitlich scrollen.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {table.isCrossTable ? (
          <CrossTable table={table} caption={caption} />
        ) : (
          <FlatTable table={table} caption={caption} />
        )}
      </div>
      {table.hasSuppressed && (
        <p className="Statistics-Note">
          „{SUPPRESSED_VALUE}“ bedeutet: weniger als 3 Fälle. Die Werte sind
          ausgeblendet, damit niemand auf einzelne Personen schließen kann.
        </p>
      )}
      {table.multiValueDimensions.length > 0 && (
        <p className="Statistics-Note">
          Bei „{table.multiValueDimensions.join('“, „')}“ kann ein Fall
          mehreren Werten zugeordnet sein. Er zählt dann in jeder dieser
          Zeilen, in der Gesamtzeile aber nur einmal.
        </p>
      )}
      <p className="Statistics-Note">„–“ bedeutet: kein Wert berechenbar.</p>
    </section>
  );
}

ResultTable.propTypes = {
  result: PropTypes.shape({
    DimensionKeys: PropTypes.arrayOf(PropTypes.string),
    MeasureKeys: PropTypes.arrayOf(PropTypes.string),
    CaseCount: PropTypes.number,
  }).isRequired,
  catalog: PropTypes.shape({
    Measures: PropTypes.arrayOf(PropTypes.shape({ Key: PropTypes.string })),
    Dimensions: PropTypes.arrayOf(PropTypes.shape({ Key: PropTypes.string })),
  }).isRequired,
  definition: PropTypes.shape({
    From: PropTypes.string,
    To: PropTypes.string,
  }),
  columnDimensionKey: PropTypes.string,
};

ResultTable.defaultProps = {
  definition: null,
  columnDimensionKey: null,
};

export default ResultTable;
